import { useEffect, useMemo, useRef } from 'react';
import styled from 'styled-components';
import { Stack, Box, useTheme } from '@mui/material';
import dayjs from 'dayjs';
import Humanize from 'humanize-plus';
import {
  CartesianGrid,
  ComposedChart,
  Bar,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { ColorToken } from '../../../style/types';
import { getColor, radius } from '../../../style/utils';
import { formatShortDateTime, localizeDate } from '../../../utils/datetime';
import { TestInvocation } from '../../../types';
import { themeTokens } from '../../../style/tokens';
import RunsChartTooltip from './RunsChartTooltip';

const DISABLE_ANIMATION_THRESHOLD = 50;

const barColorMap: Record<string, ColorToken> = {
  fail: 'chart.status.danger',
  warn: 'chart.status.warning',
  pass: 'chart.status.success',
  error: 'chart.status.danger',
  skipped: 'chart.neutral',
  no_data: 'chart.neutral',
};

const barHoverColorMap: Partial<Record<string, ColorToken>> = {
  fail: 'status.error.fill',
  warn: 'status.warning.fill',
  pass: 'status.success.fill',
  error: 'status.error.fill',
};

const Wrapper = styled(Stack)`
  border-radius: ${radius('lg')};
  background-color: ${({ theme }) => theme.palette.surface.subtle};
  width: 100%;
`;

const BarWrapper = styled(Stack)`
  width: 100%;
  height: 100%;
  justify-content: center;
  align-items: flex-end;
`;

const BarElement = styled(Box)<{
  width: number;
  height: number;
  status: string;
}>`
  height: ${({ height }) => height}px;
  width: ${({ width }) => width}px;
  background: ${({ theme, status }) => getColor(theme, barColorMap[status])};
  border: 1px solid transparent;
  border-radius: ${radius('sm', 'sm', 0, 0)};
  &:hover {
    border: ${({ theme, status }) => {
      const hoverColor = barHoverColorMap[status];
      return hoverColor && `3px solid ${getColor(theme, hoverColor)}`;
    }};
    border-bottom: none;
    box-sizing: content-box;
    border-radius: ${radius('lg', 'lg', 0, 0)};
    background-clip: padding-box;
    -webkit-background-clip: padding-box;
  }
`;

const renderTooltip = (props: any) => <RunsChartTooltip {...props} />;

const ChartBar = ({ x, y, height, width, payload }: any) => (
  <foreignObject x={x - 2.5} y={y - 3} height={height + 3} width={width + 6}>
    <BarWrapper direction="row">
      <BarElement status={payload.status} width={width} height={height} />
    </BarWrapper>
  </foreignObject>
);

type Props = {
  data: TestInvocation[];
  type: string;
};

const TestRunsChart = ({ data, type }: Props) => {
  const theme = useTheme();
  const scrollContainerRef = useRef<any>();

  const isElementaryType = useMemo(() => type !== 'dbt_test', [type]);

  const normalizedData = useMemo(
    () =>
      Array.isArray(data)
        ? [...data]
            .sort(
              (a, b) =>
                dayjs(a.time_utc).valueOf() - dayjs(b.time_utc).valueOf(),
            )
            .map((item) => ({
              ...item,
              affected_rows: item.affected_rows || 0,
            }))
        : [],
    [data],
  );

  useEffect(() => {
    if (scrollContainerRef.current) {
      scrollContainerRef.current.scrollLeft =
        scrollContainerRef.current?.scrollWidth;
    }
  }, [normalizedData.length]);

  return (
    <Wrapper direction="row">
      <ComposedChart
        data={normalizedData}
        margin={{ bottom: 46, top: 30, left: 14 }}
        height={isElementaryType ? 100 : 300}
        width={isElementaryType ? 16 : 80}
      >
        {!isElementaryType && (
          <YAxis
            axisLine={false}
            tickLine={false}
            tickMargin={6}
            dataKey="affected_rows"
            allowDecimals={false}
            tick={{ fontSize: themeTokens.fontSize.md }}
            tickFormatter={(value) => Humanize.compactInteger(value)}
          />
        )}
      </ComposedChart>

      <Box
        sx={{ overflowX: 'auto', overflowY: 'hidden', flex: 1 }}
        ref={scrollContainerRef}
      >
        <ResponsiveContainer
          minWidth={normalizedData.length * 40}
          width="99%"
          height={isElementaryType ? 100 : 300}
        >
          <ComposedChart
            data={normalizedData}
            margin={{ top: 28, right: 20, bottom: 14 }}
          >
            {!isElementaryType && (
              <CartesianGrid stroke={theme.palette.chart.gridLine} />
            )}

            <XAxis
              axisLine={false}
              tickLine={false}
              tickMargin={6}
              tickFormatter={(value) =>
                dayjs(value).isValid() && value !== 0
                  ? formatShortDateTime(localizeDate(value))
                  : ''
              }
              dataKey="time_utc"
              tick={{ fontSize: themeTokens.fontSize.xs }}
            />

            {!isElementaryType && (
              <YAxis
                axisLine={false}
                tickLine={false}
                tickMargin={6}
                dataKey="affected_rows"
                allowDecimals={false}
                tick={{ fontSize: themeTokens.fontSize.md }}
                hide
                tickFormatter={(value) => Humanize.compactInteger(value)}
              />
            )}

            <Tooltip
              content={(props) =>
                renderTooltip({ ...props, floatTooltip: isElementaryType })
              }
              cursor={!isElementaryType}
              isAnimationActive={!isElementaryType}
              wrapperStyle={{
                outline: 0,
                visibility: isElementaryType ? 'collapse' : 'visible',
              }}
            />

            <Bar
              dataKey="affected_rows"
              barSize={10}
              minPointSize={14}
              activeBar={false}
              shape={<ChartBar />}
              isAnimationActive={
                !!(normalizedData.length < DISABLE_ANIMATION_THRESHOLD)
              }
            />
          </ComposedChart>
        </ResponsiveContainer>
      </Box>
    </Wrapper>
  );
};

export default TestRunsChart;
